import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { BucketService } from '../../../services/bucket.service';
import { ChatService } from '../../../services/chat.service';
import { CourseService } from '../../../services/course.service';
import { DataSourcesService } from '../../../services/data-sources.service';
import { GRAIN_PULSE_DURATION_MS, GrainPulseService } from '../../../services/grain-pulse.service';
import {
  CourseKBRow,
  Tutor,
  TutorService,
  TutorSettingItem,
  TutorTemplate,
} from '../../../services/tutor.service';
import {
  AI_SETTINGS_SECTIONS,
  HIDDEN_VARIABLES,
  SettingsSection,
  SettingsTile,
  VIRTUAL_BOT_SETTINGS_BY_VAR,
  VirtualBotSetting,
  filterSectionsByAvailableSettings,
  withAnchoredSettings,
} from '../../../pages/tutors/tutor-id/ai-settings/ai-settings.sections';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

/**
 * Step-by-step wizard for the `alpha_flexible` template.
 *
 * Keeps the flat tutor-settings page for legacy templates; this component is
 * only rendered when the user picks a template whose settings declare
 * `group_key` values (i.e. alpha_flexible today). The backend-driven
 * `visibility_rule` controls which fields appear per step.
 */
@Component({
  selector: 'app-tutor-wizard',
  templateUrl: './tutor-wizard.component.html',
  styleUrl: './tutor-wizard.component.scss',
})
export class TutorWizardComponent implements OnInit, OnDestroy {
  @Input() onComplete = (tutor: Tutor) => {};

  @ViewChild('wizardCard', { read: ElementRef })
  private wizardCardRef?: ElementRef<HTMLElement>;
  private cardFlashTimeoutId: number | null = null;

  /**
   * If set, the wizard runs in edit mode: skip creation, pre-populate
   * meta + settings + course-KB matrix from the existing tutor, and on
   * submit only PATCH settings + replace the matrix. Used when the
   * wizard is opened after a fresh "create + open wizard" decision in
   * the basic tutor dialog.
   */
  @Input() existingTutor?: Tutor;

  templates: TutorTemplate[] = [];
  settings: TutorSettingItem[] = [];
  /** Lookup `variable_name` → setting, used by section-driven rendering. */
  settingsByVar: Record<string, TutorSettingItem> = {};

  metaForm: FormGroup;
  settingsForm: FormGroup;
  courseKbRows: FormArray;

  loading = false;

  /** Sections that drive the stepper. Subset of AI_SETTINGS_SECTIONS that
   *  contain at least one visible setting for the active template. The
   *  step order matches the AI configuration page exactly so admins find
   *  the same grouping in both places. */
  visibleSections: SettingsSection[] = [];

  /** Tracks the currently displayed step index — used to highlight the
   *  matching phase header above the Nebular stepper. Updated via
   *  `(selectionChange)` on the stepper. */
  currentStepIndex = 0;

  /** Three logical phases the wizard groups into:
   *   1. Allgemein   — identity, persona, prompt blocks (basis_persona)
   *   2. Konfiguration — knowledge, model, didaktik, learner profile, guardrails
   *   3. Zuweisung & Review — knowledge-base assignment + final review
   *  The Nebular stepper itself is flat; this layout drives the
   *  custom phase indicator rendered above the stepper. */
  readonly PHASE_LABEL_KEYS = [
    'tutor.wizard.phases.general',
    'tutor.wizard.phases.configuration',
    'tutor.wizard.phases.assignment',
  ];

  // ── Phase layout (cached, NOT computed via getters) ─────────────────
  // Previously these were getters that allocated fresh arrays/strings
  // on every change-detection pass. With ~120 settings rendered across
  // the stepper that pushed Angular into a CD storm — every cycle
  // returned new array references which `*ngFor` and binding-equality
  // treat as "changed", forcing destroy + recreate of all child views.
  // Now they're fields, recomputed only when `visibleSections` changes
  // (see `recomputePhaseLayout`).
  phaseStepCounts: [number, number, number] = [1, 0, 2];
  phaseStartIndices: [number, number, number] = [0, 1, 1];
  phaseGridTemplate = '1fr 1fr 1fr';

  /** Total number of stepper steps. */
  get totalSteps(): number {
    const [a, b, c] = this.phaseStepCounts;
    return a + b + c;
  }

  /** Phase index (0/1/2) the current step belongs to. Compares against
   *  start indices in reverse so the last matching phase wins. Cheap
   *  number-comparison only — safe to compute on every CD pass. */
  get currentPhaseIndex(): number {
    const [_g, c, a] = this.phaseStartIndices;
    if (this.currentStepIndex >= a) return 2;
    if (this.currentStepIndex >= c) return 1;
    return 0;
  }

  /** Stepper handler — pulses the page-background grain field on forward
   *  navigation as a small confirmation cue, then flashes the card border
   *  white once the grain wave has run through (timing matches
   *  `GRAIN_PULSE_DURATION_MS`). */
  onStepChange(event: { index: number }): void {
    if (event.index > this.currentStepIndex) {
      this.grainPulse.pulse();
      this.scheduleCardFlash();
    }
    this.currentStepIndex = event.index;
  }

  private scheduleCardFlash(): void {
    if (this.cardFlashTimeoutId !== null) {
      window.clearTimeout(this.cardFlashTimeoutId);
    }
    this.cardFlashTimeoutId = window.setTimeout(() => {
      this.cardFlashTimeoutId = null;
      const el = this.wizardCardRef?.nativeElement;
      if (!el) return;
      // Re-trigger the CSS animation: remove → reflow → add. Without the
      // forced reflow consecutive forward-clicks within the animation
      // window would fire only once.
      el.classList.remove('is-flashing');
      void el.offsetWidth;
      el.classList.add('is-flashing');
    }, GRAIN_PULSE_DURATION_MS);
  }

  ngOnDestroy(): void {
    if (this.cardFlashTimeoutId !== null) {
      window.clearTimeout(this.cardFlashTimeoutId);
      this.cardFlashTimeoutId = null;
    }
  }

  /** Per-tile caches keyed by the tile object reference. Filled once
   *  per `buildSettingsForm` call so the template-bound `getTileGroups`
   *  / `getTileVirtualSettings` / `getTileSettings` lookups become O(1)
   *  array reads instead of fresh-array builders. Without this the
   *  wizard hangs on alpha_flexible because every CD cycle rebuilds
   *  every tile's groups+virtuals → identity-changed `*ngFor` items →
   *  child-view destruction storm.
   *
   *  WeakMap not used so the maps survive HMR + are easily inspectable.
   *  Tiles are static module-level objects so identity-keying is safe. */
  private tileGroupsCache = new Map<
    SettingsTile,
    { leader: TutorSettingItem; deps: TutorSettingItem[] }[]
  >();
  private tileVirtualCache = new Map<SettingsTile, VirtualBotSetting[]>();
  private tileSettingsCache = new Map<SettingsTile, TutorSettingItem[]>();

  /** Recompute the cached phase layout fields. Called from
   *  `buildSettingsForm` whenever `visibleSections` is reassigned. */
  private recomputePhaseLayout(): void {
    const generalCount = 1;
    const configCount = this.visibleSections.length;
    const assignmentCount = 2;
    this.phaseStepCounts = [generalCount, configCount, assignmentCount];
    this.phaseStartIndices = [
      0,
      generalCount,
      generalCount + configCount,
    ];
    if (this.phaseStepCounts.every((n) => n === 0)) {
      this.phaseGridTemplate = '1fr 1fr 1fr';
    } else {
      this.phaseGridTemplate = this.phaseStepCounts
        .map((n) => `${Math.max(n, 1)}fr`)
        .join(' ');
    }
  }

  /** Refill the per-tile caches from the current settings + sections.
   *  Order: visibleSections must already be set, settingsByVar must
   *  already be populated. Called once per buildSettingsForm. */
  private rebuildTileCaches(): void {
    this.tileGroupsCache.clear();
    this.tileVirtualCache.clear();
    this.tileSettingsCache.clear();
    for (const sec of this.visibleSections) {
      for (const tile of sec.tiles) {
        this.tileSettingsCache.set(tile, this._computeTileSettings(tile));
        this.tileGroupsCache.set(tile, this._computeTileGroups(tile));
        this.tileVirtualCache.set(tile, this._computeTileVirtual(tile));
      }
    }
  }

  constructor(
    protected fb: FormBuilder,
    protected dialogRef: NbDialogRef<TutorWizardComponent>,
    private dialogService: NbDialogService,
    public tutorService: TutorService,
    public dataSourcesService: DataSourcesService,
    protected filesService: BucketService,
    protected chatService: ChatService,
    public courseService: CourseService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private grainPulse: GrainPulseService
  ) {
    this.metaForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      template_id: ['', Validators.required],
      visual_name: [''],
      welcome_message: [''],
      widget_setting_id: [''],
      // Virtual setting __bot_ai_hint maps to bots.ai_hint column.
      // Rendered in the Guardrails section (Disclaimer tile) but
      // persisted via tutor meta, not via template_settings.
      ai_hint: [false],
    });
    this.settingsForm = this.fb.group({});
    this.settingsForm.valueChanges.subscribe(() => this.syncTopicRequired());
    this.courseKbRows = this.fb.array([]);
  }

  /** „Themenspezifisch" needs a „Fachgebiet" — without one the backend runs
   *  the agent as „Nur Wissensbasis" (al-tutor
   *  `session.normalize_knowledge_mode_settings`). In that mode the field is
   *  required, so the global required-field feedback marks it; like every
   *  other required setting it does not block saving (see `onSubmit`). */
  private syncTopicRequired(): void {
    const topic = this.settingsForm.get('expert_topic');
    if (!topic) return;
    const need = this.settingsForm.get('knowledge_mode')?.value === 'strict_topic';
    if (need === topic.hasValidator(Validators.required)) return;
    if (need) topic.addValidators(Validators.required);
    else if (!this.settings.find((s) => s.variable_name === 'expert_topic')?.is_required) {
      topic.removeValidators(Validators.required);
    } else return;
    topic.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Resolve a `template_settings.default_value` for the form control's
   * initial value. The DB carries either a plain pre-fill string OR an
   * i18n key of the shape `templateDefaults.<template>.<field>`
   * (introduced in migration 039 so admins on EN locale see English
   * suggestions instead of hardcoded German). When the lookup yields
   * the key itself (no translation registered), we fall back to the
   * raw value — so older templates without an i18n entry keep working.
   */
  /** Moodle-Aktionen sind auf der Agent-Seite ein eigener Abschnitt
   *  (`moodle_actions`, Reiter unter Verhalten & Fähigkeiten, 29.09.2026).
   *  Im Assistenten bleiben sie wie vorher eine Kachel mit Titel im Schritt
   *  „Lern-Interface" (`functions`) statt eines eigenen Schritts. */
  private withMoodleInFunctions(sections: SettingsSection[]): SettingsSection[] {
    const moodle = sections.find((s) => s.key === 'moodle_actions');
    if (!moodle) return sections;
    const moodleTiles: SettingsTile[] = moodle.tiles.map((t) => ({
      ...t,
      titleKey: 'enums.aiConfigurationTiles.moodle_tools',
      hintKey: 'enums.aiConfigurationTiles.moodle_tools_hint',
    }));
    const hasFunctions = sections.some((s) => s.key === 'functions');
    const functions =
      sections.find((s) => s.key === 'functions') ??
      AI_SETTINGS_SECTIONS.find((s) => s.key === 'functions')!;
    const merged: SettingsSection = { ...functions, tiles: [...functions.tiles, ...moodleTiles] };
    return sections.flatMap((s) => {
      if (s.key === 'functions') return [merged];
      if (s.key === 'moodle_actions') return hasFunctions ? [] : [merged];
      return [s];
    });
  }

  private resolveDefault(raw: unknown): string {
    if (typeof raw !== 'string' || !raw) return (raw as any) ?? '';
    if (!this.isTemplateDefaultKey(raw)) return raw;
    const translated = this.translate.instant(raw);
    return translated && translated !== raw ? translated : raw;
  }

  private isTemplateDefaultKey(raw: unknown): raw is string {
    return typeof raw === 'string' && raw.startsWith('templateDefaults.');
  }

  private applyResolvedDefault(
    raw: unknown,
    onResolved: (text: string) => void,
  ): void {
    if (!this.isTemplateDefaultKey(raw)) return;
    this.translate.get(raw).subscribe((translated: string) => {
      if (translated && translated !== raw) onResolved(translated);
    });
  }

  ngOnInit() {
    this.dataSourcesService.listDataSources().subscribe();
    this.chatService.listChatSettings().subscribe();
    this.courseService.listAllCourses().subscribe();

    // Edit mode needs the full metadata list so legacy templates still
    // resolve to a label. Create mode must use the creatable endpoint so
    // super-admin client grants apply and retired templates stay hidden.
    const templates$ = this.existingTutor
      ? this.tutorService.listTutorTemplates()
      : this.tutorService.listCreatableTutorTemplates();

    templates$.subscribe((templates) => {
      this.templates = templates;
      if (this.existingTutor) return;
      const flexible = templates.find((t) => t.name === 'alpha_flexible');
      if (flexible) {
        this.metaForm.controls['template_id'].setValue(flexible.id);
        this.loadTemplateSettings(flexible.id);
      }
    });

    if (this.existingTutor) {
      this.initEditMode(this.existingTutor);
    }
  }

  /** Edit mode: pre-fill meta + settings + course-KB matrix from DB. */
  private initEditMode(tutor: Tutor) {
    // Mark loading so the stepper waits for the section list to be
    // populated before mounting (see the *ngIf guard in the template).
    // Without this, the stepper would mount immediately with
    // `visibleSections=[]` and Nebular's internal step indicator
    // would never pick up the dynamically-added steps.
    this.loading = true;
    this.metaForm.patchValue({
      name: tutor.name,
      description: tutor.description,
      template_id: tutor.template_id || '',
      visual_name: tutor.visual_name || '',
      welcome_message: (tutor as any).welcome_message || '',
      widget_setting_id: tutor.widget_setting_id || '',
      ai_hint: !!tutor.ai_hint,
    });
    // Lock template — wizard should not retemplate an existing tutor.
    this.metaForm.controls['template_id'].disable();

    // Load tutor-instance settings (with their saved values) and reuse the
    // form-build path. Then patch in the saved values per variable_name.
    this.tutorService.getTutorSettings(tutor.id).subscribe({
      next: () => {
        const items = this.tutorService.tutorSettings$.value;
        this.settings = items;
        this.buildSettingsForm(items);
        const patch: Record<string, any> = {};
        for (const s of items) {
          if (!s.variable_name) continue;
          if (s.value !== undefined && s.value !== null) {
            const raw = s.value;
            const resolved = this.resolveDefault(raw);
            patch[s.variable_name] = resolved;
            // Only re-patch when the bundle finally resolves the raw
            // `templateDefaults.…` key the bot was seeded with at
            // creation. Once the user has typed (or cleared) the
            // field, `s.value` is no longer a key and this branch
            // is a no-op via `applyResolvedDefault`'s isKey guard.
            this.applyResolvedDefault(raw, (translated) => {
              const ctrl = this.settingsForm.get(s.variable_name!);
              const cur = ctrl?.value;
              if (cur === raw || cur === resolved) {
                ctrl?.setValue(translated, { emitEvent: false });
              }
            });
          }
        }
        this.settingsForm.patchValue(patch, { emitEvent: false });
        // Diagnostic — visible in browser DevTools when admins debug a
        // wizard run. Cheap and rare (once per wizard open).
        // eslint-disable-next-line no-console
        console.info(
          `[tutor-wizard] loaded ${items.length} settings for tutor ${tutor.id}; ` +
            `sections=${this.visibleSections.map((s) => s.key).join(',')}; ` +
            `controls=${Object.keys(this.settingsForm.controls).length}`
        );
        // Drop the loading flag AFTER buildSettingsForm so the stepper's
        // *ngIf guard mounts only once `visibleSections` is populated —
        // gives Nebular a stable QueryList from the first render.
        this.loading = false;
        // Force a render so the section-driven *ngFor sees the new visible
        // sections and the freshly-added FormControls bind on the same tick.
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
      },
    });

    // Pre-load existing course-KB matrix into the FormArray.
    this.tutorService.getCourseKBMatrix(tutor.id).subscribe((rows) => {
      while (this.courseKbRows.length) this.courseKbRows.removeAt(0);
      for (const row of rows) {
        this.courseKbRows.push(
          this.fb.group({
            course_id: [row.course_id, Validators.required],
            kb_id: [row.kb_id ?? ''],
            // Optional dozent-purpose KB. Only rendered as a column
            // when the active tutor has lecturer_mode on; backend
            // pairs it with the tutor row by course_id.
            kb_dozent_id: [row.kb_dozent_id ?? ''],
            moodle_placement_id: [row.moodle_placement_id ?? ''],
            is_active: [row.is_active ?? true],
          })
        );
      }
    });
  }

  onTemplateChange(templateId: string) {
    this.loadTemplateSettings(templateId);
  }

  private loadTemplateSettings(templateId: string) {
    this.loading = true;
    this.tutorService.getTemplateSettings(templateId).subscribe({
      next: (settings) => {
        this.settings = settings;
        this.buildSettingsForm(settings);
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  private buildSettingsForm(settings: TutorSettingItem[]) {
    // Index by variable_name so section/tile rendering can look up settings
    // in O(1). Hidden variables (model_name, temperature, kb_filter_by_course)
    // are dropped here so they never appear in the UI.
    const byVar: Record<string, TutorSettingItem> = {};
    for (const s of settings) {
      if (!s.variable_name) continue;
      if (HIDDEN_VARIABLES.has(s.variable_name)) continue;
      byVar[s.variable_name] = s;
    }
    this.settingsByVar = byVar;

    // Show only sections that have at least one applicable setting for the
    // active template. Mirrors the AI configuration page so the stepper
    // grouping matches what admins see on the regular settings page.
    // The `isLinkableTutor` context surfaces virtual sections (like the
    // dozent-link picker on alpha_tutor / alpha_flexible) that don't have
    // template_settings of their own.
    this.visibleSections = this.withMoodleInFunctions(
      filterSectionsByAvailableSettings(settings, {
        isLinkableTutor: this.isLinkableTutor(),
      })
    );

    // Mutate the existing FormGroup in place rather than reassigning it.
    // The HTML's `[formGroup]="settingsForm"` directives are evaluated
    // when the steps render; reassigning the reference can leave already-
    // rendered child views bound to a stale form so typing doesn't take.
    const existingControls = new Set(Object.keys(this.settingsForm.controls));
    const wantedControls = new Set<string>();
    for (const s of settings) {
      if (!s.variable_name) continue;
      if (HIDDEN_VARIABLES.has(s.variable_name)) continue;
      wantedControls.add(s.variable_name);
      if (!existingControls.has(s.variable_name)) {
        const validators = s.is_required ? [Validators.required] : [];
        const initial = this.resolveDefault(s.default_value);
        this.settingsForm.addControl(
          s.variable_name,
          this.fb.control(initial, validators),
        );
        // Async re-patch: `translate.instant` returns the raw key when
        // the HTTP-loaded i18n bundle hasn't arrived yet. Wait for it
        // via `translate.get` and overwrite the control once — but
        // ONLY when it still holds the unresolved raw key. We must
        // NOT trigger on `cur === ''` because in edit mode the
        // subsequent `initEditMode` patch loop legitimately sets the
        // control to '' when the user has previously cleared the
        // field; re-injecting the default here would defeat the
        // dummy-text "shown once" policy.
        const raw = s.default_value;
        if (this.isTemplateDefaultKey(raw)) {
          this.applyResolvedDefault(raw, (translated) => {
            const cur = this.settingsForm.get(s.variable_name!)?.value;
            if (cur === raw) {
              this.settingsForm
                .get(s.variable_name!)
                ?.setValue(translated, { emitEvent: false });
            }
          });
        }
      }
    }
    // Drop stale controls (e.g. when switching templates mid-create)
    for (const name of existingControls) {
      if (!wantedControls.has(name)) this.settingsForm.removeControl(name);
    }

    // Refresh the cached layout/tile data — the template binds to these
    // caches instead of calling array-allocating helpers per CD pass.
    this.syncTopicRequired();
    this.recomputePhaseLayout();
    this.rebuildTileCaches();
  }

  /**
   * Evaluate a setting's visibility_rule against the current form values.
   * Rule shape: { when: "<variable_name>", equals: <value> } — matches what
   * migration 010 + 011 write into the DB.
   */
  isVisible(setting: TutorSettingItem): boolean {
    const rule = setting.visibility_rule;
    if (!rule) return true;
    const actual = this.settingsForm.get(rule.when)?.value;
    if ('equals' in rule) return actual === rule.equals;
    if ('not_equals' in rule) return actual !== rule.not_equals;
    return true;
  }

  addCourseKbRow() {
    this.courseKbRows.push(
      this.fb.group({
        course_id: ['', Validators.required],
        kb_id: [''],
        kb_dozent_id: [''],
        moodle_placement_id: [''],
        is_active: [true],
      })
    );
  }

  removeCourseKbRow(i: number) {
    this.courseKbRows.removeAt(i);
  }

  /** Settings (in declared order) for one tile. Reads from the cache
   *  filled in `buildSettingsForm` — keeps the array reference stable
   *  across CD passes so the template's `*ngIf` count check doesn't
   *  thrash. Per-field `*ngIf="isVisible(setting)"` still applies in
   *  the inner field renderer to handle dynamic visibility rules. */
  getTileSettings(tile: SettingsTile): TutorSettingItem[] {
    return this.tileSettingsCache.get(tile) ?? [];
  }

  /** Tile groups (toggle leader + dependent settings). Cached. See
   *  the docstring on `_computeTileGroups` for the grouping rule. */
  getTileGroups(tile: SettingsTile): {
    leader: TutorSettingItem;
    deps: TutorSettingItem[];
  }[] {
    return this.tileGroupsCache.get(tile) ?? [];
  }

  /** Virtual bot-backed settings (e.g. `__bot_ai_hint`) for one tile.
   *  Cached. */
  getTileVirtualSettings(tile: SettingsTile): VirtualBotSetting[] {
    return this.tileVirtualCache.get(tile) ?? [];
  }

  // ── Cache builders (called once per `buildSettingsForm`) ────────────
  // These return fresh arrays — that's expected because they only run
  // when the section/setting layout actually changes, not on every CD
  // pass. The public `getTile…` methods above just hand back the
  // cached references.

  /** Per-tile settings list, kept stable across CD passes. NOTE: we
   *  no longer apply `isVisible` here because visibility_rule depends
   *  on form values that change at runtime (e.g. flip a toggle to
   *  reveal a sub-field). The template handles per-field visibility
   *  via the inner `<ng-container *ngIf="isVisible(setting)">` guard. */
  private _computeTileSettings(tile: SettingsTile): TutorSettingItem[] {
    const items: TutorSettingItem[] = [];
    for (const v of this._tileVariables(tile)) {
      const s = this.settingsByVar[v];
      if (s) items.push(s);
    }
    return items;
  }

  /** Group each enable-toggle with the settings it gates so the
   *  template can render them inside a single `.field` box.
   *
   *  A "group" is `{ leader, deps[] }`:
   *    - `leader` is always rendered.
   *    - `deps` are settings declared *after* the leader in the tile
   *      whose `visibility_rule.when` matches the leader's variable —
   *      the existing pattern for `*_enabled` toggles + their numeric
   *      partner. They render conditionally (driven by `isVisible`).
   *
   *  Settings without a leader (no toggle gates them) become groups
   *  with no deps, so the template loop is uniform.
   *
   *  We do NOT filter by `isVisible(leader)` here because that would
   *  require recomputing the cache on every form-value change. The
   *  template's per-field `*ngIf="isVisible(setting)"` already hides
   *  the field; we just keep the structural list stable. */
  private _computeTileGroups(tile: SettingsTile): {
    leader: TutorSettingItem;
    deps: TutorSettingItem[];
  }[] {
    const groups: { leader: TutorSettingItem; deps: TutorSettingItem[] }[] = [];
    for (const v of this._tileVariables(tile)) {
      const s = this.settingsByVar[v];
      if (!s) continue;
      const ruleVar = s.visibility_rule?.when;
      // Attach to the most recent group whose leader is the dependency
      // target. Leaders themselves are pushed as new groups.
      const owner =
        ruleVar &&
        [...groups]
          .reverse()
          .find((g) => g.leader.variable_name === ruleVar);
      if (owner) {
        owner.deps.push(s);
      } else {
        groups.push({ leader: s, deps: [] });
      }
    }
    return groups;
  }

  /** The tile's variables plus settings anchored to one of them
   *  (`ANCHORED_SETTINGS`, e.g. „Formell" after the style field). */
  private _tileVariables(tile: SettingsTile): string[] {
    return withAnchoredSettings(tile.variables, (v) => !!this.settingsByVar[v]);
  }

  /** Virtual bot-backed settings (e.g. `__bot_ai_hint`) declared by
   *  this tile, mapped through `VIRTUAL_BOT_SETTINGS_BY_VAR`. */
  private _computeTileVirtual(tile: SettingsTile): VirtualBotSetting[] {
    return tile.variables
      .map((v) => VIRTUAL_BOT_SETTINGS_BY_VAR[v])
      .filter((v): v is VirtualBotSetting => !!v);
  }

  /** Markdown-light example body for a virtual setting. Reuses the
   *  same `enums.aiConfigurationExamples.<name>` namespace so admins
   *  see consistent toggle content regardless of whether the setting
   *  is virtual or backed by template_settings. */
  virtualExampleMarkup(v: VirtualBotSetting): string {
    if (!v?.name) return '';
    const key = 'enums.aiConfigurationExamples.' + v.name;
    const raw = this.translate.instant(key) as string;
    if (!raw || raw === key) return '';
    return this.renderInlineBold(raw);
  }

  async onSubmit() {
    // Mark all controls touched so error styling shows on the missing
    // fields. Block submit only when meta is invalid — required template
    // settings show a `status="danger"` border in the field renderer but
    // shouldn't prevent saving (admins often want to persist a half-set
    // configuration and finish later).
    this.metaForm.markAllAsTouched();
    this.settingsForm.markAllAsTouched();
    if (this.metaForm.invalid) return;
    this.loading = true;

    if (this.existingTutor) {
      this.applyChanges(this.existingTutor);
    } else {
      this.createAndApply();
    }
  }

  private createAndApply() {
    const tutorPayload: any = { ...this.metaForm.value };
    this.tutorService
      .createTutor(tutorPayload)
      .pipe(
        switchMap((d: any) => {
          const tutor = d.data as Tutor;
          return this.persistSettingsAndMatrix(tutor).pipe(
            switchMap(() => of(tutor))
          );
        })
      )
      .subscribe({
        next: (tutor) => {
          this.onComplete(tutor);
          this.loading = false;
          this.dialogRef.close();
        },
        error: () => (this.loading = false),
      });
  }

  private applyChanges(tutor: Tutor) {
    // Edit mode: patch tutor meta (name, description, visual_name,
    // welcome_message, widget_setting_id) + settings + matrix. The
    // template_id is locked in edit mode and excluded from the payload.
    const { template_id, ...metaPatch } = this.metaForm.getRawValue();
    const updatedTutor: Tutor = { ...tutor, ...metaPatch };

    // Strip empty-string UUID fields — TutorUpdateModel expects
    // Optional[UUID] and Pydantic v2 fails to coerce '' → UUID with 422.
    // Also drop fields that aren't part of TutorUpdateModel to keep the
    // payload tight. created_at/updated_at as Date objects also confuse
    // the validator.
    const sanitized: any = { ...updatedTutor };
    for (const key of [
      'widget_setting_id',
      'data_source_ids',
      'tutor_data_source_ids',
      'dozent_data_source_ids',
      'endpoint_id',
      'client_id',
      'template_id',
    ]) {
      if (sanitized[key] === '' || sanitized[key] === undefined) {
        sanitized[key] = null;
      }
    }
    delete sanitized.created_at;
    delete sanitized.updated_at;

    this.tutorService
      .updateTutor(sanitized)
      .pipe(switchMap(() => this.persistSettingsAndMatrix(updatedTutor)))
      .subscribe({
        next: () => {
          this.onComplete(updatedTutor);
          this.loading = false;
          this.dialogRef.close();
        },
        error: () => (this.loading = false),
      });
  }

  private persistSettingsAndMatrix(tutor: Tutor) {
    // Backend `TutorSettingsModel` expects a `{settings: {<setting_id>: value}}`
    // map keyed by setting_id (UUID-string), not a list. Building a list
    // returns 422 from FastAPI — match the AI-Konfig page's PATCH shape.
    const settingsMap: Record<string, any> = {};
    for (const [name, value] of Object.entries(this.settingsForm.value)) {
      const setting = this.settings.find((s) => s.variable_name === name);
      if (!setting) continue;
      // An untouched „Einschränkungen der Wissensbasis“ is left to the
      // backend: a new agent switches to „Nur Wissensbasis“ by itself once
      // it gets knowledge (migration 224) — the course-KB matrix below runs
      // in parallel and would race a re-sent default.
      if (name === 'knowledge_mode' && this.settingsForm.get(name)?.pristine) continue;
      settingsMap[String(setting.setting_id)] = this.resolveDefault(value);
    }

    const calls: any[] = [
      this.tutorService.updateTutorSettings(tutor.id, settingsMap),
    ];

    const rows: CourseKBRow[] = this.courseKbRows.value
      // Drop rows that don't carry at least one KB — they're empty
      // placeholders the user added but never filled in.
      .filter(
        (r: CourseKBRow) => r.course_id && (r.kb_id || r.kb_dozent_id)
      )
      .map((r: CourseKBRow) => ({
        course_id: r.course_id,
        kb_id: r.kb_id || null,
        kb_dozent_id: r.kb_dozent_id || null,
        moodle_placement_id: r.moodle_placement_id || null,
        is_active: r.is_active ?? true,
      }));
    // Always replace — user may have removed all rows on purpose.
    calls.push(this.tutorService.replaceCourseKBMatrix(tutor.id, rows));

    return forkJoin(calls);
  }

  /**
   * True when an inline `enums.aiConfigurationExamples.<name>` translation
   * exists. Drives the "Beispiel + Wozu"-block under prompt/text fields.
   */
  hasExample(setting: TutorSettingItem): boolean {
    if (!setting?.name) return false;
    const key = 'enums.aiConfigurationExamples.' + setting.name;
    const resolved = this.translate.instant(key);
    return !!resolved && resolved !== key;
  }

  /** Render the markdown-light example text as HTML (only **bold**). */
  exampleMarkup(setting: TutorSettingItem): string {
    if (!setting?.name) return '';
    const key = 'enums.aiConfigurationExamples.' + setting.name;
    const raw = this.translate.instant(key) as string;
    return this.renderInlineBold(raw);
  }

  /** Single i18n key for every disclosure toggle — keeping it uniform
   *  is clearer than splitting between "Beispiel" and "Hinweis"
   *  (the toggle content itself decides which is appropriate). */
  readonly EXAMPLE_TOGGLE_KEY = 'tutor.wizard.exampleToggle';

  /** Bold-formatted example/why-text for the meta fields (Tutor-Name,
   *  admin description, learner-facing display name, welcome message,
   *  chat styling). These don't live in `template_settings` so they
   *  don't go through hasExample/exampleMarkup. */
  metaExample(
    field: 'name' | 'description' | 'displayName' | 'welcomeMessage' | 'chatStyle'
  ): string {
    const key = `tutor.wizard.metaExamples.${field}`;
    const raw = this.translate.instant(key) as string;
    if (!raw || raw === key) return '';
    return this.renderInlineBold(raw);
  }

  /** Markdown-light renderer for the example/hint disclosure body.
   *  Supports:
   *    - **bold** → <strong>
   *    - blank line (\n\n) → paragraph break
   *    - line starting with "- " → bullet list item (consecutive lines
   *      get grouped into a single <ul>)
   *  Everything is HTML-escaped first so user-controlled translation
   *  strings can't inject markup. */
  private renderInlineBold(raw: string): string {
    if (!raw) return '';
    const escaped = raw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const blocks = escaped.split(/\n\s*\n/);
    const html = blocks
      .map((block) => this.renderBlock(block))
      .filter((s) => !!s)
      .join('');
    return html;
  }

  /** Render a single block separated by blank lines. A block can be:
   *   - A "labeled" block: first line is just `**Label:**`, remaining
   *     lines belong to it as body. The label renders block-level with
   *     no margin so the body line sits directly underneath, and the
   *     whole block carries the spacing to the next block.
   *   - A bullet list: every line starts with "- ". May also be preceded
   *     by a label-only first line.
   *   - A plain paragraph: lines joined with <br>, **bold** stays inline. */
  private renderBlock(block: string): string {
    const trimmed = block.trim();
    if (!trimmed) return '';

    const lines = trimmed.split('\n').map((l) => l.trimEnd());

    // Detect a label-only first line (e.g. `**Beispiel:**`). That label
    // becomes a block-level header for the rest of the block.
    const labelMatch = /^\s*\*\*([^*]+?)\*\*\s*$/.exec(lines[0]);
    let labelHtml = '';
    let body = lines;
    if (labelMatch) {
      labelHtml = `<strong class="field__example-label">${labelMatch[1]}</strong>`;
      body = lines.slice(1);
    }

    if (body.length === 0) {
      // Label only, no body — render the label alone in its own block.
      return `<div class="field__example-section">${labelHtml}</div>`;
    }

    const allBullets = body.every((l) => /^\s*-\s+/.test(l));

    if (allBullets) {
      const items = body
        .map((l) => l.replace(/^\s*-\s+/, ''))
        .map((item) => `<li>${this.applyBold(item)}</li>`)
        .join('');
      return `<div class="field__example-section">${labelHtml}<ul class="field__example-list">${items}</ul></div>`;
    }

    // Plain paragraph body. Single \n between body lines becomes <br>;
    // **bold** stays inline (only block-leading labels are block-level).
    const paragraph = body.map((l) => this.applyBold(l)).join('<br>');
    return `<div class="field__example-section">${labelHtml}<p class="field__example-para">${paragraph}</p></div>`;
  }

  private applyBold(text: string): string {
    return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  }

  /**
   * Resolved template name for the read-only label shown in edit mode.
   * Reads from the loaded `templates` list (cached) and falls back to the
   * raw template_id while loading.
   */
  get activeTemplateLabel(): string {
    const id = this.metaForm.get('template_id')?.value;
    if (!id) return '';
    const t = this.templates.find((t) => t.id === id);
    return t ? t.name : '';
  }

  /** Templates offered in the create dropdown. In create mode `templates`
   *  comes from the backend creatable endpoint; in edit mode the dropdown
   *  is not rendered and `templates` is the full metadata list for labels. */
  get creatableTemplates(): TutorTemplate[] {
    return this.templates;
  }

  /** True when the active template can attach a dozent (alpha_tutor /
   *  alpha_flexible). Drives whether the dozent-link picker tile shows
   *  up in the Dozent step. */
  isLinkableTutor(): boolean {
    const id = this.metaForm.get('template_id')?.value;
    if (!id) return false;
    const t = this.templates.find((t) => t.id === id);
    return !!t && (t.name === 'alpha_tutor' || t.name === 'alpha_flexible');
  }

  /** True when an alpha_dozent bot is currently attached to this tutor.
   *  Read from the listing snapshot (`existingTutor.attached_dozent_id`)
   *  so the kb-matrix's Dozent column appears whenever a dozent is in
   *  play. The picker keeps the link in sync with its own API calls;
   *  this flag re-reads on each change-detection so it stays accurate
   *  while the user is still in the wizard. */
  get hasAttachedDozent(): boolean {
    return !!this.existingTutor?.attached_dozent_id;
  }

  /** Picker callback — keeps `existingTutor.attached_dozent_id` in sync
   *  with the latest backend state so reactive UI bound to
   *  `hasAttachedDozent` (e.g. the KB-matrix's Dozent column) updates
   *  on the same tick. */
  onDozentLinkChanged(dozentBotId: string | null): void {
    if (!this.existingTutor) return;
    this.existingTutor = {
      ...this.existingTutor,
      attached_dozent_id: dozentBotId,
    };
  }

  showMetaError(name: string) {
    return (
      this.metaForm.get(name)?.touched && this.metaForm.get(name)?.invalid
    );
  }

  cancel() {
    // Confirm before discarding wizard input. Edit-mode would otherwise
    // silently drop unsaved settings changes; create-mode would lose the
    // entire form. Skip the prompt only if everything is still pristine.
    const dirty = this.metaForm.dirty || this.settingsForm.dirty;
    if (!dirty) {
      this.dialogRef.close();
      return;
    }
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'tutor.wizard.cancelConfirm.title',
          message: 'tutor.wizard.cancelConfirm.message',
        },
      })
      .onClose.subscribe((confirmed) => {
        if (confirmed) this.dialogRef.close();
      });
  }

  /** True while submit should remain disabled — `loading` covers both the
   *  in-flight create call and the post-create settings PATCH. The form
   *  validity is intentionally NOT a precondition; required fields are
   *  marked with status="danger" by the field renderer instead, so admins
   *  can still hit submit and see what's missing. */
  get submitDisabled(): boolean {
    return this.loading;
  }
}
